import {
  autoUpdate,
  FloatingPortal,
  flip,
  hide,
  offset,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { DotsThreeIcon } from '@phosphor-icons/react'
import { useState } from 'react'

import { BorderLoader } from '@/components/ui/loaders/border-loader'

import { Icon } from '../icon'
import { DEFAULT_DROPDOWN_MENU_OFFSET, TRIGGER_ICON_SIZE_MAP } from './dropdown-menu.constants'
import * as S from './dropdown-menu.styled'
import { DropdownMenuItem, DropdownMenuProps } from './dropdown-menu.types'

export const DropdownMenuTrigger = ({
  size = 'small',
  trigger,
  isOpen,
  ...rest
}: Omit<DropdownMenuProps, 'items'> & { isOpen: boolean }) => {
  if (!trigger)
    return (
      <S.TriggerButton $size={size} type="button" {...rest}>
        <Icon icon={DotsThreeIcon} size={TRIGGER_ICON_SIZE_MAP[size]} weight="bold" />
      </S.TriggerButton>
    )
  return trigger({ isOpen })
}

export const DropdownMenu = ({
  className,
  items,
  size = 'small',
  trigger,
  menuOffset = DEFAULT_DROPDOWN_MENU_OFFSET,
  dropdownZIndex,
  onOpenChange,
  ...props
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    middleware: [
      offset(menuOffset),
      flip(),
      hide(),
      sizeMiddleware({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    onOpenChange: (open) => {
      setIsOpen(open)
      onOpenChange?.(open)
    },
    open: isOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'dialog' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  // Note: Hide the dropdown when the reference element is clipped (scrolled out of view)
  const isHidden = middlewareData.hide?.referenceHidden

  const close = () => setIsOpen(false)

  const handleItemClick = async (item: DropdownMenuItem) => {
    await item.onClick?.()
    close()
  }

  return (
    <S.Container className={className} {...props}>
      <S.TriggerWrapper ref={refs.setReference} {...getReferenceProps()}>
        <DropdownMenuTrigger size={size} trigger={trigger} isOpen={isOpen} />
      </S.TriggerWrapper>

      {isOpen && !isHidden && (
        <FloatingPortal>
          <S.MenuContainer
            ref={refs.setFloating}
            $zIndex={dropdownZIndex}
            style={{
              ...floatingStyles,
              // Note: Override the z-index for the table component
              zIndex: dropdownZIndex,
            }}
            {...getFloatingProps()}
          >
            <S.Menu>
              {items.map((item, index) => {
                if (item.hidden) return null

                const isLoading = item.loading ?? false
                const isDisabled = item.disabled || isLoading

                return (
                  <S.MenuItem
                    key={index}
                    $isDisabled={isDisabled}
                    $variant={item.variant}
                    disabled={isDisabled}
                    type="button"
                    onClick={() => {
                      if (isDisabled) return
                      void handleItemClick(item)
                    }}
                  >
                    {item.icon && (
                      <S.MenuItemIconWrapper $isDisabled={isDisabled} $variant={item.variant}>
                        <Icon icon={item.icon} size="1.6rem" />
                      </S.MenuItemIconWrapper>
                    )}
                    <S.MenuItemText>{item.label}</S.MenuItemText>
                    {item.badge && (
                      <S.MenuItemBadge color="blue" variant="light" size="extra-small">
                        {item.badge}
                      </S.MenuItemBadge>
                    )}
                    {isLoading && (
                      <S.MenuItemLoaderOverlay>
                        <BorderLoader align="absolute-center" size="extra-small" />
                      </S.MenuItemLoaderOverlay>
                    )}
                  </S.MenuItem>
                )
              })}
            </S.Menu>
          </S.MenuContainer>
        </FloatingPortal>
      )}
    </S.Container>
  )
}
